import React, { useState } from "react";
import { Link } from "react-router-dom";
import { DE, deGlow1, deGrid, dePageBg } from "./deTheme";
import "./premium-ui.css";

const NAV = [
  { id: "book", icon: "🚜", label: "Book Machine" },
  { id: "calculator", icon: "💰", label: "Cost" },
  { id: "mybookings", icon: "📋", label: "Bookings" },
  { id: "tracking", icon: "📍", label: "Track" },
];

const MORE = [
  { id: "wallet", icon: "💳", label: "Wallet" },
  { id: "reports", icon: "📊", label: "Reports" },
  { id: "alerts", icon: "🔔", label: "Alerts" },
  { id: "support", icon: "🆘", label: "Support" },
];

const ALL_TABS = [...NAV, ...MORE];

const MACHINES = [
  {
    name: "JCB 3DX Super",
    type: "Backhoe Loader",
    regNo: "MH-12-DE-701",
    specs: "4-in-1 Bucket · AC Cabin",
    fuel: 78,
    available: true,
    rates: { perHour: 2200, perDay: 16500, perWeek: 95000, perMonth: 320000 },
    advance: { hourly: "2", weekly: "3", monthly: "7" },
    operator: "Ramesh Patil",
    operatorExp: 12,
    operatorReviews: 84,
    operatorRating: 4.9,
  },
  {
    name: "ACE 12T Crane",
    type: "Mobile Crane",
    regNo: "MH-12-DE-204",
    specs: "12 Ton · 22m Boom",
    fuel: 41,
    available: false,
    rates: { perHour: 3500, perDay: 28000, perWeek: 165000, perMonth: 520000 },
    advance: { hourly: "2", weekly: "3", monthly: "7" },
    operator: "Suresh Jadhav",
    operatorExp: 9,
    operatorReviews: 61,
    operatorRating: 4.7,
  },
];

/**
 * Faithful DE brand preview — same visual language as ClientDashboard / Login.
 * Not a generic template. Production code unchanged until you approve.
 */
export default function DeBrandPreview() {
  const [activeTab, setActiveTab] = useState("book");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const currentTab = ALL_TABS.find((t) => t.id === activeTab) || NAV[0];

  return (
    <div style={{ ...dePageBg, display: "flex", flexDirection: "column" }} className="premium-ui de-brand-preview">
      <div style={deGrid} aria-hidden />
      <div style={deGlow1} aria-hidden />

      {/* Banner */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          padding: "12px 16px",
          borderBottom: `1px solid ${DE.border}`,
          background: "rgba(5,13,26,0.95)",
          textAlign: "center",
        }}
      >
        <p style={{ margin: 0, color: DE.gold, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>
          DEVELOPMENT EXPRESS — Brand preview (तुमच्या app सारखेच)
        </p>
        <p style={{ margin: "4px 0 0", color: DE.muted, fontSize: 10 }}>
          जुना cyan demo काढला. Approve नंतरच real dashboards वर लागू.
        </p>
        <Link
          to="/login"
          style={{ display: "inline-block", marginTop: 8, color: DE.gold, fontSize: 11, fontWeight: 600 }}
        >
          ← Classic Login
        </Link>
      </div>

      {/* Phone viewport */}
      <div className="de-preview-viewport">
        <div style={s.shell}>
          {/* Top bar — same as MobileNav */}
          <header style={s.topbar}>
            <div style={s.topLeft}>
              <div style={s.logoSm}>DE</div>
              <div style={{ minWidth: 0 }}>
                <p style={s.topTitle}>Development Express</p>
                <p style={s.topSub}>Client Portal</p>
              </div>
            </div>
            <button type="button" style={s.menuBtn} onClick={() => setDrawerOpen(!drawerOpen)} aria-label="Menu">
              ☰
            </button>
          </header>

          {drawerOpen && (
            <div style={s.drawerOverlay} onClick={() => setDrawerOpen(false)}>
              <aside style={s.drawer} onClick={e => e.stopPropagation()}>
                <p style={s.drawerHint}>More sections (repeat नाही)</p>
                {MORE.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    style={activeTab === item.id ? s.navActive : s.nav}
                    onClick={() => {
                      setActiveTab(item.id);
                      setDrawerOpen(false);
                    }}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </aside>
            </div>
          )}

          <main style={s.main}>
            <div style={s.header}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h2 style={s.pageTitle}>
                  {currentTab.icon} {currentTab.label}
                </h2>
                <p style={s.pageDate}>
                  {new Date().toLocaleDateString("en-IN", {
                    weekday: "long",
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>
              <div style={s.clientBadge}>
                <span style={s.onlineDot} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  Patil Builders
                </span>
              </div>
            </div>

            {activeTab === "book" && (
              <>
                <div style={s.infoBanner}>
                  <p style={{ margin: 0, color: DE.gold, fontSize: 11 }}>
                    <strong>Wallet-Only Policy:</strong> Advance via wallet. No cash on site.
                  </p>
                </div>
                <div style={s.cardRow}>
                  {[
                    { icon: "🚜", val: "3", label: "Available Now" },
                    { icon: "💳", val: "₹42K", label: "Wallet Balance" },
                    { icon: "📋", val: "2", label: "Active Bookings" },
                    { icon: "⭐", val: "4.78", label: "Avg Rating" },
                  ].map((c, i) => (
                    <div key={i} style={s.statCard}>
                      <p style={s.statIcon}>{c.icon}</p>
                      <h3 style={s.statVal}>{c.val}</h3>
                      <p style={s.statLbl}>{c.label}</p>
                    </div>
                  ))}
                </div>
                <h3 style={s.sectionTitle}>Available Machinery</h3>
                {MACHINES.map((m, i) => (
                  <MachineCard key={i} m={m} onBook={() => setActiveTab("calculator")} />
                ))}
              </>
            )}

            {activeTab === "calculator" && (
              <div style={s.placeholderPanel}>
                <p style={s.placeholderIcon}>💰</p>
                <p style={s.placeholderTitle}>Cost & Booking</p>
                <p style={s.placeholderText}>Select machine, dates, and confirm advance from wallet.</p>
                <button type="button" style={s.selectBtn} onClick={() => setActiveTab("book")}>
                  View Fleet
                </button>
              </div>
            )}

            {activeTab === "mybookings" && (
              <div style={s.placeholderPanel}>
                <p style={s.placeholderIcon}>📋</p>
                <p style={s.placeholderTitle}>My Bookings</p>
                <p style={s.placeholderText}>2 active bookings · 1 awaiting dispatch</p>
              </div>
            )}

            {activeTab === "tracking" && (
              <div style={s.placeholderPanel}>
                <p style={s.placeholderIcon}>📍</p>
                <p style={s.placeholderTitle}>Live Tracking</p>
                <p style={s.placeholderText}>JCB-001 · Karad, Satara · Fuel 72%</p>
              </div>
            )}

            {activeTab === "wallet" && (
              <div style={s.placeholderPanel}>
                <p style={s.placeholderIcon}>💳</p>
                <p style={s.placeholderTitle}>Wallet & Pay</p>
                <p style={s.placeholderText}>Balance ₹42,000 · UPI recharge available</p>
              </div>
            )}

            {activeTab === "reports" && (
              <div style={s.placeholderPanel}>
                <p style={s.placeholderIcon}>📊</p>
                <p style={s.placeholderTitle}>Reports</p>
                <p style={s.placeholderText}>GST invoices and booking summary PDFs</p>
              </div>
            )}

            {activeTab === "alerts" && (
              <div style={s.placeholderPanel}>
                <p style={s.placeholderIcon}>🔔</p>
                <p style={s.placeholderTitle}>Alerts</p>
                <p style={s.placeholderText}>Dispatch updates, wallet low balance, policy notices</p>
              </div>
            )}

            {activeTab === "support" && (
              <div style={s.placeholderPanel}>
                <p style={s.placeholderIcon}>🆘</p>
                <p style={s.placeholderTitle}>Support</p>
                <p style={s.placeholderText}>Call +91-8408000084 · WhatsApp · Email DE office</p>
              </div>
            )}
          </main>

          <nav style={s.bottomNav}>
            {NAV.map(item => (
              <button
                key={item.id}
                type="button"
                style={s.bottomBtn(activeTab === item.id)}
                onClick={() => setActiveTab(item.id)}
              >
                <span style={{ fontSize: 18 }}>{item.icon}</span>
                <span style={s.bottomLbl}>{item.label.split(" ")[0]}</span>
              </button>
            ))}
            <button
              type="button"
              style={s.bottomBtn(MORE.some((m) => m.id === activeTab) || drawerOpen)}
              onClick={() => setDrawerOpen(true)}
            >
              <span style={{ fontSize: 18 }}>⋯</span>
              <span style={s.bottomLbl}>More</span>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

function MachineCard({ m, onBook }) {
  return (
    <div style={{ ...s.machineCard, opacity: m.available ? 1 : 0.6, marginBottom: 16 }}>
      <div style={s.machineHeader}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={s.machineName}>{m.name}</p>
          <p style={s.machineType}>
            {m.type} · {m.regNo}
          </p>
          <p style={{ color: DE.muted, fontSize: 10, margin: "2px 0 0" }}>{m.specs}</p>
        </div>
        <span
          style={{
            ...s.availBadge,
            background: m.available ? "rgba(76,175,80,0.15)" : "rgba(233,69,96,0.15)",
            border: `1px solid ${m.available ? DE.green : DE.red}`,
            color: m.available ? DE.green : DE.red,
          }}
        >
          {m.available ? "Available" : "Deployed"}
        </span>
      </div>
      <div style={s.fuelRow}>
        <span style={{ color: DE.muted, fontSize: 11, width: 40 }}>Fuel:</span>
        <div style={s.fuelBarBg}>
          <div
            style={{
              ...s.fuelBarFill,
              width: `${m.fuel}%`,
              background: m.fuel > 50 ? DE.green : m.fuel > 25 ? "#FF9800" : DE.red,
            }}
          />
        </div>
        <span style={{ color: m.fuel > 50 ? DE.green : DE.red, fontSize: 12, fontWeight: 700 }}>{m.fuel}%</span>
      </div>
      <div style={s.rateBox}>
        <p style={s.rateTitle}>Rate Card (Operator Included)</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 5 }}>
          {[
            { val: `₹${m.rates.perHour.toLocaleString("en-IN")}`, lbl: "/hr" },
            { val: `₹${m.rates.perDay.toLocaleString("en-IN")}`, lbl: "/day" },
            { val: `₹${m.rates.perWeek.toLocaleString("en-IN")}`, lbl: "/wk" },
            { val: `₹${m.rates.perMonth.toLocaleString("en-IN")}`, lbl: "/mo" },
          ].map((r, j) => (
            <div key={j} style={s.rateCell}>
              <p style={s.rateVal}>{r.val}</p>
              <p style={s.rateLbl}>{r.lbl}</p>
            </div>
          ))}
        </div>
      </div>
      {m.available ? (
        <button type="button" style={s.selectBtn} onClick={onBook}>
          Book This Machine
        </button>
      ) : (
        <div style={s.deployed}>Currently Deployed</div>
      )}
    </div>
  );
}

const s = {
  shell: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    background: DE.bg,
  },
  topbar: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "calc(env(safe-area-inset-top, 0px) + 10px) 12px 10px",
    borderBottom: `1px solid ${DE.borderStrong}`,
    background: DE.panelFlat,
  },
  topLeft: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  logoSm: {
    width: 32,
    height: 32,
    background: DE.logoGrad,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: 11,
    color: DE.navy,
    flexShrink: 0,
  },
  topTitle: { color: DE.gold, fontWeight: 700, fontSize: 12, margin: 0 },
  topSub: { color: DE.muted, fontSize: 9, margin: 0 },
  menuBtn: {
    background: "rgba(201,168,76,0.1)",
    border: `1px solid ${DE.borderStrong}`,
    color: DE.gold,
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 16,
    cursor: "pointer",
    flexShrink: 0,
  },
  drawerOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 50,
    background: "rgba(0,0,0,0.7)",
    display: "flex",
  },
  drawer: {
    width: 260,
    maxWidth: "85%",
    height: "100%",
    background: "linear-gradient(180deg, #0f2040, #0a1628)",
    padding: 12,
    borderRight: `1px solid ${DE.border}`,
    overflowY: "auto",
  },
  drawerHint: { color: DE.muted, fontSize: 10, margin: "0 0 8px", letterSpacing: 1 },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "12px 14px",
    marginBottom: 4,
    borderRadius: 8,
    border: "none",
    background: "transparent",
    color: DE.muted,
    fontSize: 13,
    cursor: "pointer",
    textAlign: "left",
  },
  navActive: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "12px 14px",
    marginBottom: 4,
    borderRadius: 8,
    border: `1px solid ${DE.borderStrong}`,
    background: "rgba(201,168,76,0.1)",
    color: DE.gold,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "left",
  },
  main: {
    flex: 1,
    minHeight: 0,
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "12px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 16,
    paddingBottom: 14,
    borderBottom: `1px solid rgba(201,168,76,0.15)`,
  },
  pageTitle: { color: DE.gold, fontWeight: 700, fontSize: 16, margin: "0 0 4px" },
  pageDate: { color: DE.muted, fontSize: 11, margin: 0 },
  clientBadge: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    maxWidth: "42%",
    background: "rgba(201,168,76,0.1)",
    border: `1px solid ${DE.borderStrong}`,
    padding: "6px 10px",
    borderRadius: 20,
    color: DE.gold,
    fontSize: 10,
    flexShrink: 0,
  },
  onlineDot: { width: 7, height: 7, background: DE.green, borderRadius: "50%", flexShrink: 0 },
  infoBanner: {
    background: "rgba(201,168,76,0.08)",
    border: `1px solid rgba(201,168,76,0.25)`,
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 14,
  },
  cardRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 10,
    marginBottom: 14,
  },
  statCard: {
    background: DE.panelFlat,
    border: `1px solid ${DE.border}`,
    borderRadius: 12,
    padding: 14,
    textAlign: "center",
  },
  statIcon: { fontSize: 20, margin: "0 0 4px" },
  statVal: { color: DE.gold, fontSize: 17, fontWeight: 700, margin: "0 0 2px" },
  statLbl: { color: DE.muted, fontSize: 10, margin: 0 },
  sectionTitle: { color: DE.gold, fontSize: 13, margin: "0 0 10px" },
  placeholderPanel: {
    background: DE.panelFlat,
    border: `1px solid ${DE.border}`,
    borderRadius: 12,
    padding: 24,
    textAlign: "center",
  },
  placeholderIcon: { fontSize: 40, margin: "0 0 10px" },
  placeholderTitle: { color: DE.gold, fontWeight: 700, fontSize: 16, margin: "0 0 8px" },
  placeholderText: { color: DE.muted, fontSize: 12, margin: "0 0 16px", lineHeight: 1.45 },
  machineCard: {
    background: DE.panelFlat,
    border: `1px solid ${DE.border}`,
    borderRadius: 12,
    padding: 16,
    maxWidth: "100%",
  },
  machineHeader: { display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 10 },
  machineName: { color: DE.gold, fontWeight: 700, fontSize: 14, margin: "0 0 2px" },
  machineType: { color: DE.muted, fontSize: 11, margin: 0 },
  availBadge: { padding: "4px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600, flexShrink: 0 },
  fuelRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 },
  fuelBarBg: { flex: 1, height: 6, background: "rgba(255,255,255,0.1)", borderRadius: 3, minWidth: 0 },
  fuelBarFill: { height: "100%", borderRadius: 3 },
  rateBox: {
    background: "rgba(201,168,76,0.05)",
    border: `1px solid rgba(201,168,76,0.15)`,
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
  },
  rateTitle: { color: "rgba(201,168,76,0.7)", fontSize: 10, letterSpacing: 1, margin: "0 0 6px" },
  rateCell: { textAlign: "center", background: "rgba(201,168,76,0.05)", borderRadius: 6, padding: "6px 2px" },
  rateVal: { color: DE.gold, fontWeight: 700, fontSize: 10, margin: "0 0 1px" },
  rateLbl: { color: DE.muted, fontSize: 9, margin: 0 },
  selectBtn: {
    width: "100%",
    padding: 12,
    background: DE.btnGrad,
    color: DE.navy,
    border: "none",
    borderRadius: 10,
    fontWeight: 800,
    fontSize: 13,
    cursor: "pointer",
    boxShadow: "0 6px 20px rgba(201,168,76,0.25)",
  },
  deployed: {
    textAlign: "center",
    padding: 8,
    background: "rgba(233,69,96,0.08)",
    borderRadius: 8,
    color: DE.red,
    fontSize: 12,
  },
  bottomNav: {
    flexShrink: 0,
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    borderTop: `1px solid ${DE.borderStrong}`,
    background: DE.panelFlat,
    padding: "6px 2px calc(env(safe-area-inset-bottom, 0px) + 6px)",
    minHeight: 56,
  },
  bottomBtn: on => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    flex: 1,
    minWidth: 0,
    maxWidth: 72,
    padding: "4px 2px",
    border: "none",
    background: "transparent",
    color: on ? DE.gold : DE.muted,
    cursor: "pointer",
  }),
  bottomLbl: {
    fontSize: 8,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};
